import { FollowerDialog } from "@/components/FollowerModal";
import GdOrgSwitchDialog, { useOrgList } from "@/components/GdOrgSwitch";
import GuideTooltip from "@/components/GuideTooltip";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import { useLogin } from "@/components/LoginModal";
import LogoutConfirmDialog from "@/components/LoginModal/logoutConfirm";
import { MuseTag } from "@/components/Muse";
import { AdditionGoodDialog, BuyVipDialog } from "@/components/Vip";
import { useUserJobName } from "@/constants/areas";
import useAppStore from "@/context/store";
import displayNum from "@/utils/displayNum";
import isEditingElement from "@/utils/isEditingElement";
import useOuterClick from "@/utils/useOuterClick";

import HBAvatar from "@/components/Avatar";
import { AB_USERINFO_SET } from "@/constants/ABMng";
import { PermissionCodes } from "@/constants/permissionCodes";
import { getExpsValue } from "@/tracker/abTest";
import { getAccessToken, isUnifiedUser } from "@/utils/auth";
import { isGdMerged, openSettingPage } from "@/utils/gaoding";
import { hasPermission } from "@/utils/hooks/usePermisson";
import { ORDER_INVOICE_PAGE } from "@/utils/host";
import gdRequest from "@/utils/requestGaoding";
import {
  useHover,
  useKeyPress,
  useLocalStorageState,
  useSessionStorageState,
} from "ahooks";
import { Button, Dropdown, Tooltip } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import Link from "next/link";
import { useRouter } from "next/router";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Case, Switch } from "react-if";
import GdPermission from "../../GdPermission";
import styles from "./index.module.less";
import { useInitRedTipState, useRedTipState } from "./redTipState";
import { VipPanel } from "./VipPanel";

dayjs.extend(duration);

export default function Avatar({ linkTo } = {}) {
  const { user, config, unPayOrders } = useAppStore(
    ({ user, config, unPayOrders }) => ({
      user,
      config,
      unPayOrders,
    })
  );
  const { logout } = useLogin();
  const avatarRef = useRef(null);
  const [dropRef, dropVisible, setDropVisible] = useOuterClick(false, {
    extraTargets: [avatarRef],
  });
  const router = useRouter();

  // 红点标识
  useInitRedTipState();
  const { couponTip, orderTip } = useRedTipState((store) => store);
  const avatarRedTip = useMemo(() => {
    return couponTip || orderTip;
  }, [couponTip, orderTip]);

  const chatDialogRef = useRef();
  // 快捷键：消息弹窗
  useKeyPress(
    "m",
    async (e) => {
      const editing = isEditingElement();
      if (editing || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) {
        return false;
      }

      if (chatDialogRef.current) {
        chatDialogRef.current.close();
        chatDialogRef.current = null;
      } else {
        const { ChatDialog } = await import("@/components/Chat");
        chatDialogRef.current = ChatDialog.open();
      }
    },
    { exactMatch: true }
  );

  const [hideOrderTooltip, setHideOrderTooltip] =
    useSessionStorageState("showOrderTooltip");

  // const isPro = user?.extra?.pro;

  const timeShowRef = useRef(null);
  const timeHideRef = useRef(null);
  const showDrop = () => {
    // setTipVisible(false);
    clearTimeout(timeShowRef.current);
    clearTimeout(timeHideRef.current);
    timeShowRef.current = setTimeout(() => {
      setDropVisible(true);
    }, 300);
  };

  const hideDrop = () => {
    clearTimeout(timeShowRef.current);
    clearTimeout(timeHideRef.current);
    timeHideRef.current = setTimeout(() => setDropVisible(false), 300);
  };

  const openSettings = async () => {
    // 已合并用户，需要判断是否是session登录还是token登录
    openSettingPage(user);
  };

  return (
    <>
      <Dropdown
        className={styles.avatar}
        getPopupContainer={(triggerNode) => triggerNode.parentElement}
        destroyPopupOnHide
        overlay={
          <AvatarDrop
            user={user}
            config={config}
            ref={dropRef}
            onHide={hideDrop}
            onShow={showDrop}
            unPayOrders={unPayOrders}
            openBuyVipDialog={() => BuyVipDialog.open()}
            openAddition={AdditionGoodDialog.open}
            linkTo={linkTo}
            openSettings={openSettings}
          />
        }
        visible={dropVisible}
        placement="bottomRight"
      >
        <GuideTooltip
          visible={
            !hideOrderTooltip &&
            unPayOrders.length > 0 &&
            !router.asPath.startsWith("/me/order")
          }
          overlayClassName={styles.orderTooltip}
          title={null}
          content={
            <a href={ORDER_INVOICE_PAGE} target="_blank">
              你有一笔待支付订单， 立即处理 <HBIcon type="ic_chevron_right" />
            </a>
          }
          placement={"bottomLeft"}
          width={"278px"}
          hideAction
          onClose={() => setHideOrderTooltip(true)}
          getPopupContainer={() => avatarRef.current}
        >
          <div
            ref={avatarRef}
            onMouseEnter={showDrop}
            onMouseLeave={hideDrop}
            className="header-item"
            data-gd-expose="button_expose"
            data-gd-click="button_click"
            data-module-name="顶部导航栏"
            data-subject-name="按钮"
            data-button-name="个人面板"
          >
            <Link href={linkTo || "/user/" + user?.urlname}>
              <a
                id="avatar-container"
                className={classNames(
                  { [styles.unread]: avatarRedTip },
                  styles.avatarWrap
                )}
              >
                <HBAvatar user={user} size={32} shape="circle" />
                {/* {!!isPro && (
                  <div className={styles.pro}>
                    <HBIcon
                      type="a-bianzu6"
                      style={{
                        backgroundImage:
                          "radial-gradient(#fff 40%, #ffffff00 20%)",
                      }}
                    />
                  </div>
                )} */}
              </a>
            </Link>

            <HBIcon type="ic_arrow_down" className={styles.downicon} />
          </div>
        </GuideTooltip>
      </Dropdown>
    </>
  );
}

export const AvatarDrop = forwardRef(function RawAvatarDrop(
  { user, config, onHide, onShow, linkTo, openSettings },
  ref
) {
  const router = useRouter();
  const isHovering = useHover(() =>
    document.querySelector(`.${styles.username}`)
  );
  //const unreadNum = useRedTipState((store) => store.dmUnread);
  const isPro = user?.extra?.pro;

  const [showCertifyTip, setShowCertifyTip] = useLocalStorageState(
    `certifyTip_user_${user?.user_id}`,
    {
      defaultValue: true,
    }
  );
  const [historyTip, setHistoryTip] = useLocalStorageState(
    `_PINIT_FRONT_USER_GUIDE_SIGN_HISTORY_TIP_`,
    {
      defaultValue: true,
    }
  );

  const OptionList = [
    {
      icon: "register",
      name: "花瓣认证",
      url: "/muse/register/",
      iconStyle: {
        fontSize: 19,
        marginTop: 2,
      },
      showRedTip: showCertifyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setShowCertifyTip(false);
        }
        window.open(evl.url, "_blank");
      },
    },
    {
      icon: "ic_pro",
      name: "花瓣Pro",
      url: isPro ? "https://pro.huaban.com/my/vip" : "https://pro.huaban.com/",
    },
    {
      icon: "ic_gzguanli",
      name: "管理关注",
      url: `/user/${user?.urlname}/following`,
    },
    {
      icon: "ic_settings",
      name: "个人设置",
      url: "/settings",
      action: openSettings,
      isBlank: true,
    },
    {
      icon: "recent_fill",
      name: "浏览记录",
      url: "/history",
      showRedTip: historyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setHistoryTip(false);
        }
        // 使用直接浏览器访问，确保每次都是直接访问逻辑
        window.location.href = evl.url;
      },
    },
    {
      icon: "ic_exit",
      name: "退出登录",
      action: () => {
        onHide();

        LogoutConfirmDialog.open();
      },
    },
  ];

  useEffect(() => {
    if (showCertifyTip) {
      if (!user?.extra?.is_museuser) {
        setShowCertifyTip(true);
      }
    }
  }, [user, showCertifyTip, setShowCertifyTip]);

  const jobName = useUserJobName(user);

  const onUserIdCopy = (id) => {
    navigator.clipboard.writeText(id);
    HBMessage.success("用户ID已复制");
  };

  const isOfficial = useMemo(() => {
    return config?.officialUsers?.split(",").includes(user?.urlname);
  }, [config, user]);

  const { orgList } = useOrgList();

  const [isConflictAvatar, setIsConflictAvatar] = useState(false);
  useEffect(() => {
    const fetchIsConflictAvatar = async () => {
      const abValue = await getExpsValue(AB_USERINFO_SET.variableName);
      if (AB_USERINFO_SET.map.old.includes(abValue)) {
        // AB旧版本，不执行
        return;
      }
      const accessToken = await getAccessToken();
      if (isGdMerged(user) && isUnifiedUser(accessToken)) {
        try {
          const res = await gdRequest({
            url: "/v3/api/common/users/avatar/conflict",
            method: "POST",
            cache: true,
            ums: true,
            ignoreMessage: true,
          });
          setIsConflictAvatar(!!res?.is_avatar_conflict);
        } catch (error) {
          console.warn(error);
        }
      }
    };
    fetchIsConflictAvatar();
  }, [user]);

  if (!user) {
    return null;
  }

  // 用户主页链接
  const userHomeLink = linkTo || `/user/${user.urlname}`;

  return (
    <div
      className={styles.avatarDrop}
      ref={ref}
      onMouseLeave={onHide}
      onMouseEnter={onShow}
      data-content-source="个人头像下拉框"
      data-dialog-title="个人头像下拉框"
      data-module-name="个人面板弹窗"
      data-subject-name="弹窗"
      data-gd-expose="dialog_expose"
    >
      <div className={styles.user}>
        <Link href={userHomeLink} passHref>
          <a
            className={classNames(styles.userLink, {
              [styles.hover]: isHovering,
            })}
          >
            <HBAvatar user={user} size={48} shape="circle" />
          </a>
        </Link>
        <div className={styles.userInfo}>
          <Link href={userHomeLink} passHref>
            <a className={styles.username}>
              <span className={styles.text}>{user.username}</span>
            </a>
          </Link>
          <Switch>
            <Case condition={isOfficial}>
              <div className={styles.official} title="花瓣官方"></div>
            </Case>
            <Case condition={user?.extra?.is_museuser}>
              <Link href={`/muse/register`} passHref>
                <a className={styles.designer}>
                  <MuseTag className={styles.museTag} user={user} size={20} />
                </a>
              </Link>
            </Case>
          </Switch>
          {isConflictAvatar && (
            <Tooltip
              placement="bottom"
              title="你当前的稿定/花瓣账号已合并为一个账号，在稿定账号体系下个人信息需要保持统一。"
            >
              <HBIcon
                onClick={openSettings}
                type="ic_Warning"
                style={{ color: "#F48B22", cursor: "pointer" }}
              />
            </Tooltip>
          )}
          <Link href={userHomeLink} passHref>
            <a className={styles.userMore}>
              <HBIcon type="ic_chevron_right" />
            </a>
          </Link>
          <span className={styles.job}>
            {jobName || (
              <Link href={"/settings"} passHref>
                <a
                  className={styles.setJob}
                  data-dialog-action="点击-设置职业"
                  data-gd-click="dialog_action"
                >
                  职业未设置
                </a>
              </Link>
            )}
            {user?.extra?.uc_id ? (
              <Tooltip
                placement="bottom"
                title={
                  <span
                    onClick={() => onUserIdCopy(user.extra.uc_id)}
                    style={{ cursor: "pointer", width: "100%" }}
                  >
                    {user.extra.uc_id} <HBIcon type="copy" />
                  </span>
                }
              >
                <span
                  className={styles.userId}
                  onClick={() => onUserIdCopy(user.extra.uc_id)}
                >
                  <span
                    className={styles.userIdText}
                    style={{
                      maxWidth:
                        orgList?.length > 1 &&
                        hasPermission(PermissionCodes.changeOrgTypeWithUser)
                          ? 120
                          : "auto",
                    }}
                  >
                    稿定ID: {user.extra.uc_id}
                  </span>
                  <HBIcon type="copy" />
                </span>
              </Tooltip>
            ) : (
              <span
                className={styles.userId}
                onClick={() => onUserIdCopy(user.user_id)}
              >
                用户ID: {user.user_id} <HBIcon type="copy" />
              </span>
            )}
          </span>
        </div>

        {orgList?.length > 1 && (
          <GdPermission code={PermissionCodes.changeOrgTypeWithUser}>
            <Button
              className={styles.switchOrgBtn}
              onClick={() => {
                GdOrgSwitchDialog.open({
                  orgList,
                });
              }}
              data-gdmerge
              data-gd-click="button_click"
              data-button-name="切换组织"
            >
              切换组织
              <HBIcon type="switch" />
            </Button>
          </GdPermission>
        )}
      </div>
      <ul className={styles.info}>
        <li data-dialog-action="点击-画板" data-gd-click="dialog_action">
          <Link href={userHomeLink} passHref>
            <a>
              <span>{displayNum(user.board_count)}</span>
              <span>画板</span>
            </a>
          </Link>
        </li>
        <li data-dialog-action="点击-采集" data-gd-click="dialog_action">
          <Link href={`${userHomeLink}/pins`} passHref>
            <a>
              <span>{displayNum(user.pin_count)}</span>
              <span>采集</span>
            </a>
          </Link>
        </li>

        <li data-dialog-action="点击-关注" data-gd-click="dialog_action">
          <Link href={`/user/${user.urlname}/following`} passHref>
            <a>
              <span>{displayNum(user.following_count)}</span>
              <span>关注</span>
            </a>
          </Link>
        </li>

        <li
          onClick={() => {
            FollowerDialog.open({ urlname: user.urlname });
          }}
          data-dialog-action="点击-粉丝"
          data-gd-click="dialog_action"
        >
          <a>
            <span>{displayNum(user.follower_count)}</span>
            <span>粉丝</span>
          </a>
        </li>
      </ul>
      <VipPanel />
      <div className={styles.operationList}>
        {OptionList.map((item) => {
          // if (item.type === "separate") {
          //   return <div key={index} className="separate"></div>;
          // }
          if (item.action) {
            return (
              <Link href={item.url || ""} key={item.name}>
                <a
                  className={styles.item}
                  onClick={(e) => {
                    e.preventDefault();
                    item.action(item);
                  }}
                  data-dialog-action={`点击-${item.name}`}
                  data-gd-click="dialog_action"
                  key={item.name}
                >
                  {item.icon && (
                    <HBIcon
                      className={classNames({
                        [styles.unread]: !!item.unreadNum,
                        [styles.redTip]: item.showRedTip,
                      })}
                      type={item.icon}
                      style={{
                        fontSize: item.iconSize || "20px",
                        color: item.iconColor || "#505153",
                        ...(item.iconStyle || {}),
                      }}
                    />
                  )}
                  <span>{item.name}</span>
                </a>
              </Link>
            );
          }

          // 其他默认item
          return (
            <Link href={item.url} key={item.name}>
              <a
                className={styles.item}
                target={item.isBlank ? "_blank" : "_self"}
                data-dialog-action={`点击-${item.name}`}
                data-gd-click="dialog_action"
              >
                {item.icon && (
                  <HBIcon
                    type={item.icon}
                    style={{
                      fontSize: item.iconSize || "20px",
                      color: item.iconColor || "#505153",
                      ...(item.iconStyle || {}),
                    }}
                    className={classNames({ [styles.redTip]: item.showRedTip })}
                  />
                )}
                <span>{item.name}</span>
              </a>
            </Link>
          );
        })}
      </div>
    </div>
  );
});
